import { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { TooltipIconButton } from 'sharedComponents/Buttons/TooltipIconButton';
import { ConfirmationDialog } from 'sharedComponents/Dialog/ConfirmationDialog';
import { ATSPrincipalAppCandidatesListTestIds } from 'data-testids/ATS';
import {
  CandidateAction,
  CandidateActionStatus,
} from 'features/Principal/types/candidatesListTypes';
import { ModalCandidateActionButtons } from './ModalCandidateActionButtons';

export const ACTION_CONFIG = {
  [CandidateAction.NOT_A_FIT]: {
    color: 'error' as const,
    label: 'Not a Fit',
    testId: ATSPrincipalAppCandidatesListTestIds.NOT_A_FIT_BUTTON,
    terminal: true,
  },
  [CandidateAction.IN_CONSIDERATION]: {
    color: 'warning' as const,
    label: 'In Consideration',
    testId: ATSPrincipalAppCandidatesListTestIds.IN_CONSIDERATION_BUTTON,
    terminal: false,
  },
  [CandidateAction.SELECTED_FOR_HIRE]: {
    color: 'primary' as const,
    label: 'Select for Hire',
    testId: ATSPrincipalAppCandidatesListTestIds.SELECTED_FOR_HIRE_BUTTON,
    terminal: true,
  },
};

export const getButtonSx = (action: CandidateAction, theme, isTerminalState, status) => {
  const config = ACTION_CONFIG[action];
  const colorMain = theme.palette[config.color].main;
  const isSelected = status === action;
  const isFaded = isTerminalState && !isSelected;

  return {
    border: `2px solid ${colorMain}`,
    mr: theme.spacing(1),
    mb: theme.spacing(0.5),
    opacity: isFaded ? 0.4 : 1,
    cursor: isFaded ? 'not-allowed' : 'pointer',
    backgroundColor: isSelected ? colorMain : 'transparent',
    color: isSelected ? theme.palette.common.white : colorMain,
    '&:hover': {
      backgroundColor: isSelected
        ? colorMain
        : isFaded
          ? 'transparent'
          : theme.palette.action.hover,
    },
  };
};

interface CandidateActionButtonsProps {
  status?: CandidateActionStatus;
  onStatusChange?: (status: CandidateActionStatus) => void;
  variant: 'list' | 'modal';
}

export const CandidateActionButtons: React.FC<CandidateActionButtonsProps> = ({
  status = null,
  onStatusChange,
  variant,
}) => {
  const theme = useTheme();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<CandidateAction | null>(null);

  const isInTerminalStatus =
    status === CandidateAction.NOT_A_FIT || status === CandidateAction.SELECTED_FOR_HIRE;
  const isSelectForHire = status === CandidateAction.SELECTED_FOR_HIRE;

  const isListTerminalState = isInTerminalStatus && variant === 'list';
  const inModalTerminalState = isInTerminalStatus && variant === 'modal';

  const handleClick = (action: CandidateAction) => {
    if (isListTerminalState || inModalTerminalState) return;
    if (status === action) return;

    if (ACTION_CONFIG[action].terminal) {
      setPendingAction(action);
      setConfirmDialogOpen(true);
    } else {
      onStatusChange?.(action);
    }
  };

  const handleConfirm = (confirmed: boolean) => {
    setConfirmDialogOpen(false);
    if (confirmed && pendingAction) {
      onStatusChange?.(pendingAction);
    }
    setPendingAction(null);
  };

  return (
    <Box
      flexDirection="column"
      display="flex"
      alignItems="center"
      onClick={(e) => e.stopPropagation()}
    >
      {variant === 'list' && (
        <Box display="flex" flexWrap="wrap">
          <TooltipIconButton
            tooltipTitle={ACTION_CONFIG[CandidateAction.NOT_A_FIT].label}
            dataTestId={ACTION_CONFIG[CandidateAction.NOT_A_FIT].testId}
            sx={getButtonSx(CandidateAction.NOT_A_FIT, theme, isListTerminalState, status)}
            onClick={() => handleClick(CandidateAction.NOT_A_FIT)}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </TooltipIconButton>
          <TooltipIconButton
            tooltipTitle={ACTION_CONFIG[CandidateAction.IN_CONSIDERATION].label}
            dataTestId={ACTION_CONFIG[CandidateAction.IN_CONSIDERATION].testId}
            sx={getButtonSx(CandidateAction.IN_CONSIDERATION, theme, isListTerminalState, status)}
            onClick={() => handleClick(CandidateAction.IN_CONSIDERATION)}
            size="small"
          >
            <HelpOutlineOutlinedIcon fontSize="small" />
          </TooltipIconButton>
          <TooltipIconButton
            tooltipTitle={ACTION_CONFIG[CandidateAction.SELECTED_FOR_HIRE].label}
            dataTestId={ACTION_CONFIG[CandidateAction.SELECTED_FOR_HIRE].testId}
            sx={getButtonSx(CandidateAction.SELECTED_FOR_HIRE, theme, isListTerminalState, status)}
            onClick={() => handleClick(CandidateAction.SELECTED_FOR_HIRE)}
            size="small"
          >
            <CheckIcon fontSize="small" />
          </TooltipIconButton>
        </Box>
      )}
      {variant === 'modal' && (
        <ModalCandidateActionButtons
          status={status}
          onClick={handleClick}
          confirmDialogOpen={confirmDialogOpen}
          onConfirm={handleConfirm}
          pendingAction={pendingAction}
          isSelectForHire={isSelectForHire}
          isInTerminalStatus={isInTerminalStatus}
        />
      )}
      {isSelectForHire && variant === 'list' && (
        <Typography
          variant="body3"
          sx={{ color: theme.palette.text.secondary, fontStyle: 'italic', mt: 0.5, fontSize: 10 }}
          data-testid={ATSPrincipalAppCandidatesListTestIds.ACTION_NOTIFIED_TEXT}
        >
          Your administrator has been notified
        </Typography>
      )}
      <ConfirmationDialog
        isOpen={confirmDialogOpen && variant === 'list'}
        title={pendingAction ? `Confirm ${ACTION_CONFIG[pendingAction].label}` : ''}
        text="This action is final and cannot be changed. Once confirmed, you will not be able to update your decision for this candidate. Would you like to continue?"
        confirmLabel="Continue"
        backLabel="Cancel"
        resultHandler={handleConfirm}
      />
    </Box>
  );
};
